import { useEffect, useRef } from 'react';

import { useInterval, useWindowEvent } from '@mantine/hooks';

import { ACTIVITY_HEARTBEAT_INTERVAL_MINUTES } from 'models';

import { InternalTrackingService } from 'services/InternalTrackingService';

const ACTIVITY_HEARTBEAT_INTERVAL_MS = ACTIVITY_HEARTBEAT_INTERVAL_MINUTES * 60 * 1000;

const LISTENER_OPTIONS = { capture: true, passive: true } as const;

/**
 * - `app.heartbeat` tracking event every interval while the tab is visible
 * - `app.activity` tracking event alongside it only when there was pointer/keyboard input
 *
 * The event metadata carries the current path and project id, so consumers can filter on those.
 */
export const useActivityHeartbeat = () => {
    const lastActivityAt = useRef(Date.now());

    const recordActivity = () => {
        lastActivityAt.current = Date.now();
    };

    useWindowEvent('pointermove', recordActivity, LISTENER_OPTIONS);
    useWindowEvent('pointerdown', recordActivity, LISTENER_OPTIONS);
    useWindowEvent('keydown', recordActivity, LISTENER_OPTIONS);
    useWindowEvent('wheel', recordActivity, LISTENER_OPTIONS);

    const beat = () => {
        if (document.hidden) {
            return;
        }

        InternalTrackingService.track('app.heartbeat');

        if (Date.now() - lastActivityAt.current <= ACTIVITY_HEARTBEAT_INTERVAL_MS) {
            InternalTrackingService.track('app.activity');
        }
    };

    useInterval(beat, ACTIVITY_HEARTBEAT_INTERVAL_MS, { autoInvoke: true });

    useEffect(() => {
        beat();
    }, []);
};
